export const ACCOUNT_RESEARCH_CATEGORY = 'Account research';

export function isPlanResearchItem(item) {
  return item?.category === ACCOUNT_RESEARCH_CATEGORY;
}

function trimIdentity(value) {
  if (value == null || value === '') return '';
  return String(value).trim();
}

function personIdentityTokens(it) {
  const tokens = [];
  const seen = new Set();
  const push = (value) => {
    const s = trimIdentity(value);
    if (!s) return;
    const k = s.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    tokens.push(s);
  };
  // Name first: shared across UUID plan rows and pool-style person_id on Done.
  push(it?.personaName || it?.personName);
  push(it?.personaId);
  push(it?.person_id);
  push(it?.persona_id);
  return tokens;
}

function accountIdentity(it) {
  return trimIdentity(it?.accountName || it?.accountId || it?.account_id).toLowerCase();
}

/** Identity keys for one card: person + account + category. Never title (LLM prose). */
export function planItemKeys(it) {
  const account = accountIdentity(it);
  const category = trimIdentity(it?.category);
  if (isPlanResearchItem(it)) {
    return [`${account}|${category}`];
  }
  const people = personIdentityTokens(it);
  if (people.length === 0) return [`|${account}|${category}`];
  return people.map((p) => `${p.toLowerCase()}|${account}|${category}`);
}

export function planItemKey(it) {
  return planItemKeys(it)[0] || '';
}

export function ackSetHasPlanItem(ackSet, it) {
  if (!ackSet || ackSet.size === 0) return false;
  const keys = planItemKeys(it);
  for (const k of keys) {
    if (ackSet.has(k)) return true;
  }
  // Legacy keys appended |title after the identity prefix.
  for (const stored of ackSet) {
    for (const k of keys) {
      if (stored.startsWith(`${k}|`)) return true;
    }
  }
  return false;
}

export function addPlanItemToAckSet(ackSet, it) {
  const next = ackSet instanceof Set ? new Set(ackSet) : new Set();
  planItemKeys(it).forEach((k) => {
    if (k) next.add(k);
  });
  return next;
}

/** Outreach top-N by score, then all un-acked research items. */
export function limitTodaysPlanForDisplay(todaysPlan, ackSet, { outreachLimit = 5 } = {}) {
  if (!todaysPlan?.planByCategory) return {};

  const all = [];
  for (const items of Object.values(todaysPlan.planByCategory)) {
    (items || []).forEach((it) => all.push(it));
  }

  const filtered = all.filter((it) => !ackSetHasPlanItem(ackSet, it));
  const research = filtered.filter((it) => isPlanResearchItem(it));
  const outreach = filtered.filter((it) => !isPlanResearchItem(it));
  const topOutreach = outreach
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .slice(0, outreachLimit);
  const selected = [...topOutreach, ...research];

  return selected.reduce((acc, it) => {
    const cat = it.category || 'Fit & Momentum';
    (acc[cat] ||= []).push(it);
    return acc;
  }, {});
}

export function sortPlanCategoryEntries(entries) {
  return [...entries].sort(([a], [b]) => {
    if (a === ACCOUNT_RESEARCH_CATEGORY && b !== ACCOUNT_RESEARCH_CATEGORY) return 1;
    if (b === ACCOUNT_RESEARCH_CATEGORY && a !== ACCOUNT_RESEARCH_CATEGORY) return -1;
    return a.localeCompare(b);
  });
}

export function translatePlanCategory(category, t) {
  if (!t) return category || ACCOUNT_RESEARCH_CATEGORY;
  const keyByCategory = {
    [ACCOUNT_RESEARCH_CATEGORY]: 'todaysPlanCategoryAccountResearch',
    'Fit & Momentum': 'todaysPlanCategoryFitMomentum',
    Outreach: 'todaysPlanCategoryOutreach',
    'New leads': 'todaysPlanCategoryNewLeads',
    'New Leads': 'todaysPlanCategoryNewLeads',
  };
  const raw = category || 'Fit & Momentum';
  const key = keyByCategory[raw];
  return key ? t(key) : raw;
}

export function getResearchGapCaption(notes, t) {
  const tag = (notes || []).find((n) => String(n).startsWith('research_gap:'));
  if (!tag) return null;
  const gap = String(tag).split(':')[1]?.trim();
  const keyByGap = {
    buyer: 'researchGapBuyer',
    champion: 'researchGapChampion',
    both: 'researchGapBoth',
  };
  const key = keyByGap[gap];
  if (key && t) return t(key);
  const captions = {
    buyer: 'No confirmed budget owner on the Sales Map',
    champion: 'No internal champion identified yet',
    both: 'Neither budget owner nor champion confirmed',
  };
  return captions[gap] || null;
}

export function toPlanHumanReasons(notes, t) {
  if (!t) return [];
  const out = [];
  (notes || []).forEach((n) => {
    if (out.length >= 3) return;
    const l = (n || '').toLowerCase();
    if (l.includes('leadership')) out.push(t('planReasonLeadership'));
    else if (l.includes('procurement')) out.push(t('planReasonProcurement'));
    else if (l.includes('live deal')) out.push(t('planReasonLiveDeal'));
    else if (l.includes('board')) out.push(t('planReasonBoard'));
    else if (l.includes('momentum')) out.push(t('planReasonMomentum'));
    else if (l.includes('persona activity')) out.push(t('planReasonPersonaActivity'));
    else if (l.includes('fit:')) out.push(t('planReasonFit'));
    else if (l.includes('decision window unknown')) out.push(t('planReasonDecisionWindowUnknown'));
    else if (l.includes('decision window (')) {
      const m = n.match(/\((\d+)\s*months?\)/i);
      out.push(m ? t('planReasonDecisionWindowMonths', { months: m[1] }) : t('planReasonDecisionWindowGeneric'));
    } else if (l.includes('pursuit')) out.push(t('planReasonPursuit'));
    else out.push(n);
  });
  return out.slice(0, 3);
}

export function planItemWhy(item) {
  return item?.why || item?.why_now || null;
}

/**
 * The card badge is a PRIORITY ranking, not urgency — `score` blends persona
 * influence with signal recency. The BE computes the band (thresholds live in
 * api/src/services/planPriority.ts) and sends it as `priorityBand`.
 *
 * The score fallback exists only for a CACHED plan created before 2026-09-03,
 * which has no band. It must use the same 0.59 / 0.48 cuts as the BE.
 */
export function planPriorityBadge(item, t) {
  const raw = String(item?.priorityBand || '').toLowerCase();
  const band = raw === 'high' || raw === 'medium' || raw === 'low'
    ? raw
    : (Number(item?.score) >= 0.59 ? 'high'
      : Number(item?.score) >= 0.48 ? 'medium'
      : 'low');

  if (band === 'high') {
    return { label: t?.('priorityHigh') || 'High priority', color: 'bg-green-100 text-green-800' };
  }
  if (band === 'medium') {
    return { label: t?.('priorityMedium') || 'Medium priority', color: 'bg-yellow-100 text-yellow-800' };
  }
  return { label: t?.('priorityLow') || 'Low priority', color: 'bg-gray-100 text-gray-800' };
}

/** @deprecated Use planPriorityBadge(item, t). Kept for cached callers that passed a numeric score. */
export function planUrgencyBadge(score, t) {
  return planPriorityBadge({ score }, t);
}

export function getResearchItemSubtitle(item, t) {
  const gap = getResearchGapCaption(item.notes, t);
  if (gap) return gap;
  const why = planItemWhy(item);
  if (!why) return null;
  const firstSentence = why.split(/(?<=[.!?])\s+/)[0]?.trim();
  return firstSentence || why;
}

export function buildPersonasTabPagePath(accountId) {
  if (accountId == null || String(accountId).trim() === '') return null;
  return `AccountFinal?id=${encodeURIComponent(String(accountId).trim())}&tab=personas`;
}
